import { supabase } from "../../lib/supabase";

/* ---------------------------------------------------------------- types */

export interface UsageCronJob {
  jobname: string;
  schedule: string;
  active: boolean;
  last_run_status: string | null;
  last_run_at: string | null;
  failures_last_24h: number;
}

export interface UsageLive {
  db_size_bytes: number;
  storage_bytes: number;
  active_connections: number;
  max_connections: number;
  cron_jobs: UsageCronJob[];
  checked_at: string;
}

export type UsageSeverity = "normal" | "attention" | "high" | "critical";

export interface UsageMetric {
  key: string;
  status: UsageSeverity;
  detail: string;
  value: number | null;
  link: string | null;
}

export interface UsageStatus {
  overall: UsageSeverity;
  metrics: UsageMetric[];
  db_pct_of_allowance: number;
  storage_pct_of_allowance: number;
  pct_billing_period_elapsed: number;
  checked_at: string;
}

export interface UsageSnapshot {
  id: number;
  plan: string | null;
  compute_size: string | null;
  project_status: string | null;
  region: string | null;
  fetched_at: string;
}

export interface UsageManualEntry {
  id: number;
  entered_by: string;
  entered_at: string;
  billing_period_start: string | null;
  billing_period_end: string | null;
  spend_cap_enabled: boolean | null;
  compute_hours_used: number | null;
  compute_hours_included: number | null;
  compute_credit_gbp: number | null;
  egress_gb: number | null;
  egress_included_gb: number | null;
  edge_function_invocations: number | null;
  edge_function_included: number | null;
  realtime_messages: number | null;
  realtime_included: number | null;
  realtime_peak_connections: number | null;
  monthly_active_users: number | null;
  estimated_cost_gbp: number | null;
  notes: string | null;
}

export interface UsageThresholds {
  id: number;
  pct_allowance_attention: number;
  pct_allowance_high: number;
  pct_allowance_critical: number;
  pct_period_elapsed_ahead_factor: number;
  spike_factor_vs_previous_day: number;
  cost_attention_gbp: number;
  cost_high_gbp: number;
  cost_critical_gbp: number;
  notes: Record<string, string>;
}

export interface AttentionItem {
  category: string;
  severity: UsageSeverity;
  title: string;
  detail: string;
  link: string;
}

/* ----------------------------------------------------------------- reads */

export async function fetchUsageLive(): Promise<UsageLive> {
  const { data, error } = await supabase.rpc("supabase_usage_live");
  if (error) throw error;
  return data as UsageLive;
}

export async function fetchUsageStatus(): Promise<UsageStatus> {
  const { data, error } = await supabase.rpc("supabase_usage_status");
  if (error) throw error;
  return data as UsageStatus;
}

export async function fetchUsageSnapshot(): Promise<UsageSnapshot | null> {
  const { data, error } = await supabase.rpc("supabase_usage_snapshot_latest");
  if (error) throw error;
  return (data as UsageSnapshot | null) ?? null;
}

export async function fetchUsageManualLatest(): Promise<UsageManualEntry | null> {
  const { data, error } = await supabase.rpc("supabase_usage_manual_latest");
  if (error) throw error;
  return (data as UsageManualEntry | null) ?? null;
}

export async function fetchUsageThresholds(): Promise<UsageThresholds> {
  const { data, error } = await supabase.rpc("supabase_usage_thresholds");
  if (error) throw error;
  return data as UsageThresholds;
}

export async function fetchItemsRequiringAttention(): Promise<AttentionItem[]> {
  const { data, error } = await supabase.rpc("items_requiring_attention");
  if (error) throw error;
  return (data as AttentionItem[] | null) ?? [];
}

/* ---------------------------------------------------------------- writes */

export async function saveUsageThresholds(
  patch: Partial<
    Pick<
      UsageThresholds,
      | "pct_allowance_attention"
      | "pct_allowance_high"
      | "pct_allowance_critical"
      | "pct_period_elapsed_ahead_factor"
      | "spike_factor_vs_previous_day"
      | "cost_attention_gbp"
      | "cost_high_gbp"
      | "cost_critical_gbp"
    >
  >,
): Promise<UsageThresholds> {
  const { data, error } = await supabase.rpc("set_supabase_usage_thresholds", { p: patch });
  if (error) throw error;
  return data as UsageThresholds;
}

export type ManualEntryInput = Partial<
  Pick<
    UsageManualEntry,
    | "billing_period_start"
    | "billing_period_end"
    | "spend_cap_enabled"
    | "compute_hours_used"
    | "compute_hours_included"
    | "compute_credit_gbp"
    | "egress_gb"
    | "egress_included_gb"
    | "edge_function_invocations"
    | "edge_function_included"
    | "realtime_messages"
    | "realtime_included"
    | "realtime_peak_connections"
    | "monthly_active_users"
    | "estimated_cost_gbp"
    | "notes"
  >
>;

export async function recordManualEntry(input: ManualEntryInput): Promise<UsageManualEntry> {
  const { data, error } = await supabase.rpc("record_supabase_usage_manual_entry", { p: input });
  if (error) throw error;
  return data as UsageManualEntry;
}

/* ------------------------------------------------------- Claude API spend
 *
 * The figures are Anthropic's own, pulled by the anthropic-usage-sync
 * edge function and stored per day. Money comes from their cost report,
 * never from multiplying our token counts by a price list we keep a copy
 * of -- that copy goes stale the day a price changes and the page would
 * go on looking authoritative.
 *
 * Postgres numerics arrive over PostgREST as strings, so everything that
 * reaches a chart or a sum is coerced here rather than at the point of
 * use.
 */

export interface AiSpendSync {
  last_attempted_at: string | null;
  last_succeeded_at: string | null;
  last_error: string | null;
  days_covered: number | null;
  earliest_day: string | null;
  latest_day: string | null;
}

export interface AiSpendThresholds {
  monthly_budget_usd: number | null;
  daily_attention_usd: number | null;
  daily_high_usd: number | null;
  notes: string | null;
  updated_at: string | null;
}

export interface AiSpendDay {
  day: string;
  usd: number;
  tokens_usd: number | null;
  web_search_usd: number | null;
  code_execution_usd: number | null;
  session_usd: number | null;
}

export interface AiSpendByModel {
  model: string;
  usd: number;
}

export interface AiTokensByModel {
  model: string;
  uncached_input: number;
  cache_read: number;
  cache_creation: number;
  output: number;
  total: number;
}

export interface AiSpendSummary {
  as_at: string;
  sync: AiSpendSync | null;
  thresholds: AiSpendThresholds | null;
  month: {
    starting: string;
    days_elapsed: number;
    days_in_month: number;
    total_usd: number;
    projected_usd: number | null;
    by_cost_type: Array<{ cost_type: string; usd: number }>;
    by_model: AiSpendByModel[];
  };
  days: AiSpendDay[];
  tokens: {
    month: {
      uncached_input: number;
      cache_read: number;
      cache_creation: number;
      output: number;
      web_search_requests: number;
    } | null;
    by_model: AiTokensByModel[];
  };
}

const num = (v: unknown): number => (v == null ? 0 : Number(v));
const numOrNull = (v: unknown): number | null => (v == null ? null : Number(v));

export async function fetchAiSpend(days = 30): Promise<AiSpendSummary> {
  const { data, error } = await supabase.rpc("anthropic_usage_summary", { p_days: days });
  if (error) throw error;
  const raw = data as AiSpendSummary;
  return {
    ...raw,
    thresholds: raw.thresholds
      ? {
          ...raw.thresholds,
          monthly_budget_usd: numOrNull(raw.thresholds.monthly_budget_usd),
          daily_attention_usd: numOrNull(raw.thresholds.daily_attention_usd),
          daily_high_usd: numOrNull(raw.thresholds.daily_high_usd),
        }
      : null,
    month: {
      ...raw.month,
      total_usd: num(raw.month.total_usd),
      projected_usd: numOrNull(raw.month.projected_usd),
      by_cost_type: (raw.month.by_cost_type ?? []).map((r) => ({ ...r, usd: num(r.usd) })),
      by_model: (raw.month.by_model ?? []).map((r) => ({ ...r, usd: num(r.usd) })),
    },
    days: (raw.days ?? []).map((d) => ({
      ...d,
      usd: num(d.usd),
      tokens_usd: numOrNull(d.tokens_usd),
      web_search_usd: numOrNull(d.web_search_usd),
      code_execution_usd: numOrNull(d.code_execution_usd),
      session_usd: numOrNull(d.session_usd),
    })),
    tokens: {
      month: raw.tokens?.month
        ? {
            uncached_input: num(raw.tokens.month.uncached_input),
            cache_read: num(raw.tokens.month.cache_read),
            cache_creation: num(raw.tokens.month.cache_creation),
            output: num(raw.tokens.month.output),
            web_search_requests: num(raw.tokens.month.web_search_requests),
          }
        : null,
      by_model: (raw.tokens?.by_model ?? []).map((r) => ({
        model: r.model,
        uncached_input: num(r.uncached_input),
        cache_read: num(r.cache_read),
        cache_creation: num(r.cache_creation),
        output: num(r.output),
        total: num(r.total),
      })),
    },
  };
}

export async function saveAiSpendThresholds(input: {
  monthly_budget_usd: number | null;
  daily_attention_usd: number | null;
  daily_high_usd: number | null;
  notes: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("anthropic_usage_set_thresholds", {
    p_monthly_budget_usd: input.monthly_budget_usd,
    p_daily_attention_usd: input.daily_attention_usd,
    p_daily_high_usd: input.daily_high_usd,
    p_notes: input.notes,
  });
  if (error) throw error;
}

/**
 * Ask the sync to run now.
 *
 * Through the pg_net relay, not supabase.functions.invoke: managed
 * devices on this fleet block direct browser POSTs to /functions/v1, so
 * invoke works on a developer's laptop and fails silently on the
 * machines the people who need this page actually use.
 *
 * The relay returns as soon as the request is dispatched, so this reports
 * that the refresh was asked for -- not that it has finished. The caller
 * re-reads the summary, whose sync block says when the figures actually
 * last landed.
 */
export async function runAiSpendSync(): Promise<void> {
  const { error } = await supabase.rpc("request_ai_usage_sync");
  if (error) throw error;
}

/* ------------------------------------------------- usage at three grains
 *
 * An hour, a day and a month answer different questions, and one of them
 * cannot carry money: Anthropic publishes cost in daily buckets and
 * nothing finer, so the hourly series has has_cost false and no money in
 * it. That is a fact about their API, not a gap to fill in with a
 * day's cost divided by its hours.
 */

export type UsageGrain = "hour" | "day" | "month";

export interface UsagePoint {
  at: string;
  /** Absent on the hourly grain, where there is no published cost. */
  usd?: number | null;
  tokens_usd?: number | null;
  web_search_usd?: number | null;
  code_execution_usd?: number | null;
  session_usd?: number | null;
  input: number;
  cache_read: number;
  cache_creation: number;
  output: number;
  total: number;
  web_search_requests?: number | null;
  /** Months only: how many days of that month we actually hold, so a
   *  part-synced month is not read as a cheap one. */
  days_with_data?: number | null;
}

export interface UsageSeries {
  grain: UsageGrain;
  has_cost: boolean;
  points: UsagePoint[];
  earliest: string | null;
}

export async function fetchUsageSeries(grain: UsageGrain, periods: number): Promise<UsageSeries> {
  const { data, error } = await supabase.rpc("anthropic_usage_series", {
    p_grain: grain,
    p_periods: periods,
  });
  if (error) throw error;
  const raw = data as UsageSeries;
  return {
    ...raw,
    points: (raw.points ?? []).map((p) => ({
      at: p.at,
      usd: numOrNull(p.usd),
      tokens_usd: numOrNull(p.tokens_usd),
      web_search_usd: numOrNull(p.web_search_usd),
      code_execution_usd: numOrNull(p.code_execution_usd),
      session_usd: numOrNull(p.session_usd),
      input: num(p.input),
      cache_read: num(p.cache_read),
      cache_creation: num(p.cache_creation),
      output: num(p.output),
      total: num(p.total),
      web_search_requests: numOrNull(p.web_search_requests),
      days_with_data: numOrNull(p.days_with_data),
    })),
  };
}
